import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Link, useParams } from "react-router";

import { EnlaceVolver } from "@/components/EnlaceVolver";
import { BotonDescargarCotizacion } from "@/components/informes/BotonDescargarCotizacion";
import { FichaDeVisado } from "@/components/informes/FichaDeVisado";
import { ModalDeEnvioAlCliente } from "@/components/informes/ModalDeEnvioAlCliente";
import { formatearMomento } from "@/lib/fechas";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useInforme } from "@/hooks/useInformes";
import type { Cotizacion, Informe } from "@/lib/informes";
import { urlDeLaEdicion } from "@/lib/origenDeNavegacion";

/**
 * Visado de cotización (Módulo G, RF-G2/G3): el detalle de revisión al que
 * lleva cada fila de la bandeja.
 *
 * Es OTRA pantalla que `/informes/:id/cotizacion/documento`, aunque muestren
 * datos parecidos, y la diferencia no es cosmética:
 *
 * - Aquella es la vista previa del papel que RECIBE EL CLIENTE. Se vuelve de
 *   ella a "Cotización enviada a revisión" —la pantalla del Módulo F— y su
 *   única acción es mirar.
 * - Esta es el puesto de trabajo del REVISOR. Vuelve a la bandeja, y tiene las
 *   dos acciones que RF-G2 y RF-G3 le dan: corregir el informe, o aprobar y
 *   dejar la cotización en manos del cliente.
 *
 * Mandar al revisor a la primera lo dejaba mirando el documento sin ninguna de
 * sus acciones y con la salida apuntando al flujo del ejecutivo.
 */
export function VisadoDeCotizacion() {
  const { id } = useParams();
  const informeId = Number(id);
  const ficha = useInforme(informeId);

  if (ficha.isPending) {
    return (
      <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-72" />
        <Skeleton className="h-[30rem] w-full max-w-3xl rounded-xl" />
      </div>
    );
  }

  if (ficha.isError || !ficha.data) {
    return (
      <div className="space-y-4 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
        <EnlaceVolver to="/revisiones">Volver a la bandeja</EnlaceVolver>
        <p className="text-destructive text-sm">
          No pudimos cargar este informe. Puede que ya no exista o que la conexión haya fallado.
        </p>
      </div>
    );
  }

  const informe = ficha.data;
  const cotizacion = informe.cotizacion ?? null;

  return (
    <div className="max-w-3xl space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <EnlaceVolver to="/revisiones">Volver a la bandeja</EnlaceVolver>

      <div className="flex flex-wrap items-baseline justify-between gap-3">
        {/* Cliente y propiedad identifican QUÉ se está visando, así que van en
            el encabezado y no dentro de la ficha, donde serían un dato más a
            verificar. El título de la pantalla lo pone el TopBar. */}
        <p className="text-ink-muted text-caption">
          {informe.cliente_nombre ?? "—"} · {informe.propiedad_nombre ?? "—"}
        </p>

        {/* RF-G1 nombra el PDF de la cotización como parte del detalle de
            revisión. Esta ficha es la de CONTROL —lo que hay que verificar—,
            así que el papel exacto que va a recibir el cliente se mira aparte;
            sin este enlace, el revisor no tenía cómo llegar a él.

            El origen viaja declarado para que el documento sepa volver ACÁ y
            no al flujo del ejecutivo. */}
        {cotizacion ? (
          <BotonDescargarCotizacion informeId={informe.id}>
            Descargar documento del cliente
          </BotonDescargarCotizacion>
        ) : null}
      </div>

      {cotizacion ? (
        <>
          <FichaDeVisado informe={informe} cotizacion={cotizacion} />
          <AccionesDeVisado informe={informe} cotizacion={cotizacion} />
        </>
      ) : (
        // La URL se puede abrir a mano, o el informe pudo cambiar en otra
        // pestaña. Se ofrece la salida útil en vez de una ficha vacía.
        <div className="space-y-4">
          <p className="text-ink-muted text-caption">
            Este informe todavía no tiene una cotización generada, así que no hay nada que visar.
          </p>
          <Button asChild variant="outline">
            <Link to={urlDeLaEdicion(informe.id, "visado")}>Ver el informe</Link>
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * El pie de la pantalla: las dos acciones del revisor, o la confirmación si ya
 * visó.
 *
 * Se ramifica por el ESTADO persistido y no por un flag local: así, volver a
 * entrar a la URL de una cotización ya aprobada muestra lo mismo que justo
 * después de aprobarla, en vez de ofrecer un botón que el backend va a
 * rechazar.
 */
function AccionesDeVisado({
  informe,
  cotizacion,
}: {
  informe: Informe;
  cotizacion: Cotizacion;
}) {
  const [confirmando, setConfirmando] = useState(false);

  if (informe.estado === "cotizacion_enviada_cliente") {
    return (
      <div className="border-success-soft-foreground/20 bg-success-soft space-y-3 rounded-xl border p-5">
        <div className="flex items-start gap-2.5">
          <CheckCircle2 className="text-success-soft-foreground mt-0.5 size-4 shrink-0" />
          <div>
            <p className="text-success-soft-foreground text-sm font-bold">
              Cotización aprobada y enviada al cliente
            </p>
            <p className="text-ink-muted mt-1 text-sm">
              {cotizacion.enviada_en
                ? `Visada el ${formatearMomento(cotizacion.enviada_en)}. `
                : ""}
              El informe queda en espera de la aceptación del cliente y salió de la bandeja de
              revisiones.
            </p>
            {/* Este texto decía lo contrario —"el correo se despacha cuando
                se habilite el módulo de correos"— y quedó viejo: el módulo
                existe y `aprobarCotizacion()` manda el correo con el PDF
                adjunto. Sostenerlo dejaba a la pantalla negando lo que la
                modal de confirmación acaba de prometer, en el mismo clic.

                SIN CORREO DE CONTACTO no se manda nada, y eso hay que decirlo
                acá: el visado se registra igual, así que sin esta línea el
                revisor se va creyendo que el cliente ya tiene el presupuesto. */}
            <p className="text-ink-hint mt-1.5 text-xs">
              {informe.email_contacto
                ? `Se le envió a ${informe.email_contacto} el correo con el PDF y el enlace para aceptarla.`
                : "Este informe no tiene correo de contacto, así que no salió ningún correo al cliente."}
            </p>
          </div>
        </div>

        <Button asChild variant="outline" size="sm">
          <Link to="/revisiones">Volver a la bandeja</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Quien no visa NO ve un botón deshabilitado sino el motivo, con el
          nombre de quien sí visa: "no podés" a secas lo deja sin saber a quién
          avisarle. El servidor manda —`puede_visar` viene calculado—; acá solo
          se dibuja lo que ya decidió. */}
      {!informe.puede_visar && (
        <p className="text-ink-muted bg-muted/50 rounded-lg border px-4 py-3 text-sm">
          {informe.revisor_cotizacion
            ? `La cotización la visa ${informe.revisor_cotizacion.nombre}, que es el revisor designado en este informe.`
            : "Este informe todavía no tiene un revisor de la cotización designado, así que nadie puede visarla."}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {/* RF-G2: desde la revisión se entra a corregir lo que esté mal. Va
            primero y en secundario — aprobar es la acción que cierra el trabajo,
            editar es el desvío. */}
        <Button asChild variant="outline">
          <Link to={urlDeLaEdicion(informe.id, "visado")}>Editar informe</Link>
        </Button>

        {/* El botón ya NO envía: abre la confirmación. Lo que dispara sale
            fuera de la organización con el precio adentro y deja al informe sin
            poder recotizarse, así que el clic que lo despacha tiene que ser el
            segundo y no el primero. */}
        {informe.puede_visar && (
          <Button onClick={() => setConfirmando(true)}>
            Aprobar y enviar cotización al cliente
          </Button>
        )}
      </div>

      <ModalDeEnvioAlCliente
        informe={informe}
        abierto={confirmando}
        onCerrar={() => setConfirmando(false)}
      />
    </div>
  );
}
